// DNA CONNECT · Documentos para assinar (mobile-first)
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileSignature, Loader2, CheckCircle2, Download, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem('familyToken');

const call = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}`, ...(opts.headers || {}) },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Falha na operação');
  return data;
};

export const DocumentosParaAssinar = () => {
  const [lists, setLists] = useState({ pendentes: [], assinados: [], arquivados: [] });
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [step, setStep] = useState('revisar');
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    call('/dna-sign/portal/documents')
      .then(setLists).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    try {
      const d = await call(`/dna-sign/portal/documents/${id}`);
      setDoc(d); setStep('revisar'); setC1(false); setC2(false);
      setScrolled(!d.require_scroll); setCode('');
    } catch (e) { toast.error(e.message); }
  };

  const pedirCodigo = async () => {
    setBusy(true);
    try {
      const r = await call(`/dna-sign/portal/documents/${doc.document_id}/otp`, {
        method: 'POST',
        body: JSON.stringify({ aceite_vontade: c1, aceite_evidencias: c2, scrolled_to_end: scrolled }),
      });
      toast.success(r.enviado ? `Código enviado para ${r.destino}` : (r.aviso || 'Código gerado'));
      setStep('otp');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const assinar = async () => {
    setBusy(true);
    try {
      const r = await call(`/dna-sign/portal/documents/${doc.document_id}/sign`, {
        method: 'POST',
        body: JSON.stringify({ code, aceite_vontade: c1, aceite_evidencias: c2, scrolled_to_end: scrolled }),
      });
      toast.success(r.status === 'COMPLETED' ? 'Documento assinado e concluído!' : 'Assinatura registrada!');
      setStep('pronto'); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const baixar = (id) => {
    fetch(`${API_URL}/api/dna-sign/portal/documents/${id}/download`,
      { headers: { Authorization: `Bearer ${tok()}` } })
      .then(r => r.blob()).then(b => {
        const u = URL.createObjectURL(b); const a = document.createElement('a');
        a.href = u; a.download = `${id}.pdf`; a.click(); URL.revokeObjectURL(u);
      }).catch(() => toast.error('Falha ao baixar'));
  };

  const total = lists.pendentes.length;
  if (loading) return <div className="p-4 flex items-center gap-2 text-slate-500">
    <Loader2 className="w-4 h-4 animate-spin" /> Carregando documentos…</div>;

  return (
    <div className="space-y-4" data-testid="connect-documentos-assinar">
      <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FileSignature className="w-6 h-6 text-violet-700" />
            <div>
              <div className="font-bold text-violet-900">DOCUMENTOS PARA ASSINAR</div>
              <div className="text-xs text-violet-700">
                {total ? `${total} documento(s) aguardando sua assinatura` : 'Nenhum documento pendente'}
              </div>
            </div>
          </div>
          {total > 0 && <Badge className="bg-violet-700">{total}</Badge>}
        </div>
        <div className="mt-3 space-y-2">
          {lists.pendentes.map(d => (
            <div key={d.document_id} className="rounded-xl bg-white border p-3" data-testid={`doc-pend-${d.document_id}`}>
              <div className="font-semibold text-sm">{d.title}</div>
              <div className="text-xs text-slate-500">
                {d.aluno} · {d.school_year} · enviado {d.enviado_em} · prazo {d.prazo}
              </div>
              <Button className="mt-2 w-full sm:w-auto" size="sm" onClick={() => open(d.document_id)}
                disabled={!d.posso_assinar} data-testid={`doc-revisar-${d.document_id}`}>
                REVISAR E ASSINAR
              </Button>
            </div>
          ))}
        </div>
      </div>

      {lists.assinados.length > 0 && (
        <div className="rounded-2xl border bg-white p-4">
          <div className="text-xs uppercase font-bold text-slate-500 mb-2">Assinados</div>
          {lists.assinados.map(d => (
            <div key={d.document_id} className="flex flex-wrap items-center justify-between gap-2 border-b py-2">
              <div>
                <div className="text-sm font-medium">{d.title}</div>
                <div className="text-xs text-slate-500">{d.aluno} · concluído {d.concluido_em}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => baixar(d.document_id)}
                data-testid={`doc-baixar-${d.document_id}`}>
                <Download className="w-3 h-3 mr-1" /> Baixar
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!doc} onOpenChange={v => !v && setDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="doc-assinar-dialog">
          <DialogHeader><DialogTitle className="text-base">{doc?.title}</DialogTitle></DialogHeader>
          {doc && step !== 'pronto' && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">{doc.aluno} · {doc.school_year} · prazo {doc.prazo}</div>
              {step === 'revisar' && (
                <>
                  <div className="h-[46vh] overflow-y-auto border rounded"
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setScrolled(true);
                    }} data-testid="doc-scroll-area">
                    {doc.pdf_base64
                      ? <iframe title="contrato" className="w-full h-[120vh]"
                          src={`data:application/pdf;base64,${doc.pdf_base64}`} />
                      : <div className="p-4 text-sm text-slate-500">Documento indisponível.</div>}
                  </div>
                  {doc.require_scroll && !scrolled && (
                    <p className="text-xs text-amber-700">Role o documento até o final para liberar a assinatura.</p>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" data-testid="doc-abrir-pdf"
                      onClick={() => {
                        const w = window.open('', '_blank');
                        if (w) w.document.write(
                          `<iframe src="data:application/pdf;base64,${doc.pdf_base64}" style="width:100%;height:100%;border:0"></iframe>`);
                        setScrolled(true);
                      }}>Abrir documento em nova aba</Button>
                  </div>
                  <label className="flex gap-2 items-start text-xs">
                    <Checkbox checked={c1} onCheckedChange={v => setC1(!!v)} data-testid="doc-aceite-1" />
                    <span>{doc.consent_texts?.vontade}</span>
                  </label>
                  <label className="flex gap-2 items-start text-xs">
                    <Checkbox checked={c2} onCheckedChange={v => setC2(!!v)} data-testid="doc-aceite-2" />
                    <span>{doc.consent_texts?.evidencias}</span>
                  </label>
                  <Button className="w-full h-12 text-base" disabled={!c1 || !c2 || !scrolled || busy}
                    onClick={pedirCodigo} data-testid="doc-pedir-otp">
                    {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Confirmar identidade e receber código
                  </Button>
                </>
              )}
              {step === 'otp' && (
                <>
                  <p className="text-sm">Enviamos um código de 6 dígitos para <b>{doc.otp_destination}</b>.
                    Ele expira em {doc.otp_ttl_minutes} minutos.</p>
                  <Input inputMode="numeric" maxLength={6} className="text-center text-2xl tracking-[10px] h-14"
                    value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000" data-testid="doc-otp-input" />
                  <Button className="w-full h-12 text-base" disabled={code.length !== 6 || busy}
                    onClick={assinar} data-testid="doc-assinar">
                    {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} ASSINAR DOCUMENTO
                  </Button>
                  <Button variant="ghost" className="w-full" onClick={pedirCodigo} disabled={busy}>
                    Reenviar código
                  </Button>
                </>
              )}
            </div>
          )}
          {step === 'pronto' && (
            <div className="text-center space-y-3 py-6" data-testid="doc-assinado-ok">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
              <div className="font-bold text-lg">Documento assinado!</div>
              <p className="text-sm text-slate-500">
                Você já pode baixar o documento final com o certificado de evidências.
              </p>
              <Button onClick={() => baixar(doc.document_id)} className="w-full">
                <Download className="w-4 h-4 mr-2" /> Baixar documento assinado
              </Button>
              <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Assinatura eletrônica DNA Sign · hash SHA-256
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentosParaAssinar;
